if (!customElements.get('mdm-sticky-atc')) {
  customElements.define('mdm-sticky-atc', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.mainButton = this.productInfo?.querySelector(`[id="ProductSubmitButton-${this.dataset.section}"]`);
      this.mainPrice = this.productInfo?.querySelector(`[id="price-${this.dataset.section}"]`);
      this.mainForm = this.mainButton?.closest('product-form');
      if (!this.mainForm || !('IntersectionObserver' in window) || this.closest('quick-add-modal')) return;
      this.submit = this.querySelector('[data-submit]');
      this.options = this.querySelector('[data-options]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.onClick = (event) => {
        if (event.target.closest('[data-options]')) this.focusOptions();
        if (event.target.closest('[data-submit]') && !this.submit.disabled) {
          this.pendingSubmission = true;
          // Keep Dawn's form validation, gift-card fields, cart events and errors.
          this.mainButton.click();
          this.sync();
        }
      };
      this.addEventListener('click', this.onClick);
      this.sync = () => {
        const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
        this.submit.disabled = this.mainButton.disabled || busy;
        this.submit.textContent = this.mainButton.querySelector('span')?.textContent.trim() || this.mainButton.textContent.trim();
        this.querySelector('[data-price]').hidden = !this.mainPrice || this.mainPrice.classList.contains('hidden');
        const price = this.mainPrice?.querySelector('.price--on-sale .price__sale .price-item--sale') || this.mainPrice?.querySelector('.price__regular .price-item--regular');
        if (price) this.querySelector('[data-price]').textContent = price.textContent.trim();
        this.hidden = !this.mobile.matches || !this.pastBuyBox;
        this.reserveSpace();
        if (this.pendingSubmission && !busy) {
          this.pendingSubmission = false;
          const error = this.productInfo.querySelector(
            '.product-form__error-message-wrapper:not([hidden]), .product-form__recipient-error-message-wrapper:not([hidden])'
          );
          if (error?.textContent.trim()) this.focusError(error);
        }
      };
      this.observer = new IntersectionObserver(([entry]) => {
        this.pastBuyBox = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.sync();
      });
      this.observer.observe(this.mainForm);
      // Anchor jumps can skip the form entirely: the observer stays non-intersecting
      // on both sides. Check that boundary once per frame during mobile scrolling.
      this.onScroll = () => {
        if (!this.mobile.matches || this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const pastBuyBox = this.mainForm.getBoundingClientRect().bottom <= 0;
          if (pastBuyBox === this.pastBuyBox) return;
          this.pastBuyBox = pastBuyBox;
          this.sync();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.mutations = new MutationObserver(this.sync);
      this.mutations.observe(this.mainForm, { attributes: true, childList: true, subtree: true, characterData: true });
      if (this.mainPrice) this.mutations.observe(this.mainPrice, { attributes: true, childList: true, subtree: true, characterData: true });
      this.mobile.addEventListener('change', this.sync);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.reserveSpace());
        this.resizeObserver.observe(this);
      }
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          const thumbnail = this.querySelector('img');
          const updated = data.html?.querySelector('mdm-sticky-atc img');
          if (thumbnail && updated) {
            thumbnail.src = updated.src;
            thumbnail.srcset = updated.srcset;
            thumbnail.alt = updated.alt;
          }
          this.sync();
        });
      }
      this.sync();
    }
    reserveSpace() {
      if (!this.mobile.matches) {
        this.releaseSpace();
        return;
      }
      const height = Math.ceil(this.getBoundingClientRect().height);
      if (!height) return;
      // Retain space while scrolling back up or opening the drawer, avoiding jumps.
      this.constructor.spaceOwner = this;
      document.documentElement.style.setProperty('--mdm-sticky-atc-height', `${height + 8}px`);
    }
    releaseSpace() {
      if (this.constructor.spaceOwner !== this) return;
      document.documentElement.style.removeProperty('--mdm-sticky-atc-height');
      this.constructor.spaceOwner = null;
    }
    focusError(error) {
      const invalid = this.productInfo.querySelector('[aria-invalid="true"]:not([disabled])');
      const target = invalid || error;
      if (!invalid) target.setAttribute('tabindex', '-1');
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
      target.focus({ preventScroll: true });
    }
    focusOptions() {
      const target = this.productInfo.querySelector('variant-selects input:not([disabled]), variant-selects select, recipient-form input:not([type="hidden"]), .quantity__input') || this.mainButton;
      (target.closest('fieldset, recipient-form, .product-form__input') || target).scrollIntoView({ behavior: 'auto', block: 'center' });
      target.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.mutations?.disconnect();
      this.resizeObserver?.disconnect();
      this.mobile?.removeEventListener('change', this.sync);
      window.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.scrollFrame);
      this.unsubscribe?.();
      this.removeEventListener('click', this.onClick);
      this.releaseSpace();
    }
  });
}
